import { useContext, useEffect, useState } from 'react'
import { SelectItem } from '@mantine/core'
import { ErrorContext } from 'context'
import { useCurrentUser } from 'context/useCurrentUser'
import { getMySchools, updateSchool } from 'api'
import { useArcManagerDashboardEnabled } from 'hooks'
import { ARC_BRANDING_STORAGE_KEY } from 'hooks/useArcBranding'

/**
 * School selector state for the Arc top bar. Mirrors the legacy NavbarMenu
 * behaviour (fetch my schools for tutor/manager, auto-select the first one
 * when none is selected — Arc-only behaviour — and hard-redirect after
 * switching) as a standalone hook so the legacy component stays untouched.
 */
export const useSchoolSelect = () => {
  const { setError } = useContext(ErrorContext)
  const { currentUser } = useCurrentUser()
  const isManagerDashboardEnabled = useArcManagerDashboardEnabled()
  const [data, setData] = useState<SelectItem[]>([])
  const [value, setValue] = useState<string | null>(null)
  const isTutorOrManager = currentUser?.profile_used === 'tutor' || currentUser?.profile_used === 'manager'
  const currentSchool = currentUser?.selected_school?.id

  const onChange = (selected: string) => {
    setValue(selected)
    updateSchool(selected)
      .then(response => {
        if (response && response.status === 200) {
          window.localStorage.removeItem('current_user')
          // The branding cache belongs to the previous school — drop it so
          // the reload resolves the arc flag for the newly selected school
          // instead of flashing the old school's chrome
          window.localStorage.removeItem(ARC_BRANDING_STORAGE_KEY)
          if (currentUser?.profile_used === 'manager') {
            window.location.replace(isManagerDashboardEnabled ? '/app/manager/dashboard' : '/manager/dashboard')
          }
          if (currentUser?.profile_used === 'tutor') window.location.replace('/teacher/dashboard')
        } else {
          setError(response.statusText)
          console.error(response.statusText)
        }
      })
      .catch(error => {
        setError(error)
        console.error(error)
      })
  }

  useEffect(() => {
    const fetchSchools = () => {
      getMySchools()
        .then(response => {
          const schools = response.data.map((school: { id: string; name: string }) => ({
            value: school.id,
            label: school.name,
          }))
          if (schools.length === 0) return
          setData(schools)
          if (currentSchool) {
            setValue(currentSchool)
          } else {
            // The top bar only renders under Arc, where the legacy menu
            // auto-selected the first school when none was picked yet
            onChange(schools[0].value)
          }
        })
        .catch(error => {
          setError(error)
          console.error(error)
        })
    }
    if (isTutorOrManager) fetchSchools()
  }, [isTutorOrManager])

  return { data, value, onChange, visible: isTutorOrManager && data.length > 1 }
}
